import { useEffect, useRef, useState, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
// import Autoplay from 'embla-carousel-autoplay'; // Disabled auto-slide to match Next.js
import Fade from 'embla-carousel-fade';
import { Calendar, ChevronRight } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';

const FeaturedCarousel = ({ posts = [] }) => {
  // Accessibility: Respect user's motion preferences
  const prefersReducedMotion = useReducedMotion();
  const animationDuration = prefersReducedMotion ? 0.2 : 0.6;

  // Single carousel instance with fade transition (auto-slide disabled to match Next.js)
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true, 
      align: 'start'
    },
    [Fade()] // Autoplay removed to match Next.js exactly
  );
  
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const scrollTo = useCallback(
    (index) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
        // Autoplay plugin removed - no reset needed
      }
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi) => {
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi) => {
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    emblaApi.on('reInit', onInit);
    emblaApi.on('select', onSelect);
  }, [emblaApi, onInit, onSelect]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!posts.length) return null;

  return (
    <section id="featured-carousel-component" className="embla relative w-full">
      {/* Single Carousel with Responsive Styling */}
      <div className="relative">
        {/* Main Carousel */}
        <div className="embla__viewport overflow-hidden" ref={emblaRef}>
          <div className="embla__container flex">
            {posts.map((post, index) => {
              return (
                <motion.div
                  key={post.id}
                  className="embla__slide flex-none w-full min-w-full relative"
                  role="tabpanel"
                  aria-label={`Slide ${index + 1} of ${posts.length}`}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{
                    opacity: 1,
                    scale: 1
                  }}
                  transition={{
                    duration: prefersReducedMotion ? 0 : 0.8,
                    ease: "easeOut"
                  }}
                >
                  {/* Clickable slide wrapper - entire slide navigates to post like listing cards */}
                  <a
                    href={post.url}
                    className="block cursor-pointer"
                    aria-label={`Read more: ${post.title}`}
                  >
                    {/* Background Image with Parallax - Responsive aspect ratio */}
                    <motion.div
                      className="aspect-[16/9] overflow-hidden relative"
                      animate={{
                        scale: 1,
                        x: 0
                      }}
                      transition={{
                        duration: prefersReducedMotion ? 0 : 1.2,
                        ease: [0.4, 0, 0.2, 1]
                      }}
                      whileHover={{
                        scale: prefersReducedMotion ? 1 : 1.02,
                        transition: { duration: 0.3 }
                      }}
                      style={{
                        backgroundImage: `url(${post.feature_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    >
                    {/* Overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                    
                    {/* Hover overlay to darken background */}
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all duration-300"></div>
                    
                    {/* Content Overlay with Progressive Blur */}
                    <div className="absolute inset-0 flex flex-col justify-between">
                      {/* Top Overlay for Post Tag and Date */}
                      <div className="flex justify-between items-start p-4 lg:p-8">
                        {/* Post Tag - Top Left */}
                        {post.primary_tag && (
                          <span 
                            className="px-2 py-1 lg:px-3 text-xs lg:text-sm font-medium text-white rounded-full tag-overlay"
                            style={{
                              backgroundColor: `${post.primary_tag.accent_color || '#ea580c'}90` // 90 for opacity
                            }}
                          >
                            {post.primary_tag.name}
                          </span>
                        )}
                        
                        {/* Date - Top Right */}
                        {post.published_at && (
                          <div className="flex items-center gap-1 lg:gap-2 text-white/80 text-xs lg:text-sm bg-black/20 backdrop-blur-sm px-2 py-1 rounded">
                            <Calendar className="w-3 h-3 lg:w-4 lg:h-4" />
                            <time dateTime={post.published_at}>
                              {formatDate(post.published_at)}
                            </time>
                          </div>
                        )}
                      </div>
                      
                      {/* Bottom Content with Progressive Blur */}
                      <div className="relative">
                        {/* Single Progressive Blur Background - Top to Bottom */}
                        <div className="absolute top-0 left-0 right-0 h-full bg-gradient-to-b from-black/30 via-transparent to-transparent backdrop-blur-xl"></div>
                        
                        {/* Post Title and Content - Full Width */}
                        <div className="relative z-10 p-4 lg:p-8 transition-all duration-300">
                          <motion.h3
                            className="text-orange-600 dark:text-orange-400 font-extrabold text-xl md:text-2xl lg:text-3xl xl:text-4xl mb-3 lg:mb-4 leading-tight font-iansui hover:text-orange-400 dark:hover:text-orange-300 transition-colors"
                            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.8), 0 1px 4px rgba(0,0,0,0.6)' }}
                            initial={{ x: -30, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ duration: animationDuration, delay: prefersReducedMotion ? 0 : 0.3, ease: "easeOut" }}
                          >
                            {post.title}
                          </motion.h3>
                          
                          {/* Post Excerpt - Hidden on mobile for space */}
                          {post.excerpt && (
                            <motion.p
                              className="hidden md:block text-gray-300 dark:text-white/90 text-sm lg:text-base line-clamp-1 lg:line-clamp-2 mb-3 lg:mb-4 leading-relaxed"
                              style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}
                              initial={{ x: 30, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              transition={{ duration: animationDuration, delay: prefersReducedMotion ? 0 : 0.4, ease: "easeOut" }}
                            >
                              {post.excerpt}
                            </motion.p>
                          )}
                          
                          {/* Read More Button - Hidden on mobile */}
                          <motion.span
                            className="hidden lg:inline-flex items-center gap-1 lg:gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-gray-700 hover:bg-gray-600 text-orange-300 font-medium text-xs lg:text-sm rounded lg:rounded-lg transition-all duration-300"
                            initial={{ y: 10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: animationDuration * 0.8, delay: prefersReducedMotion ? 0 : 0.5, ease: "easeOut" }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            Read More
                            <ChevronRight className="w-3 h-3 lg:w-4 lg:h-4" />
                          </motion.span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  </a>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Responsive Navigation - Thumbnails on desktop, dots on mobile */}
        <div className="mt-4 lg:mt-8">
          {/* Desktop Thumbnail Navigation */}
          <div className="hidden lg:flex gap-4 justify-center lg:justify-start" role="tablist">
            {scrollSnaps.map((_, index) => (
              <motion.button
                key={index}
                className={`w-24 h-14 lg:w-28 lg:h-16 xl:w-32 xl:h-[58px] rounded-lg overflow-hidden cursor-pointer border-3 transition-all duration-200 ease-in-out focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 ${
                  index === selectedIndex 
                    ? 'border-orange-500 scale-105 shadow-lg' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-orange-400 hover:scale-102'
                }`}
                type="button"
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                role="tab"
                aria-selected={index === selectedIndex}
                style={{
                  backgroundImage: `url(${posts[index]?.feature_image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderWidth: '3px'
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {/* Thumbnail overlay for better contrast */}
                <div className={`w-full h-full ${
                  index === selectedIndex ? 'bg-black/20' : 'bg-black/40'
                }`}></div>
              </motion.button>
            ))}
          </div>

          {/* Mobile Navigation Dots */}
          <div className="flex lg:hidden justify-center gap-2" role="tablist">
            {scrollSnaps.map((_, index) => (
              <motion.button
                key={index}
                className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-200 ease-in-out focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 ${
                  index === selectedIndex 
                    ? 'bg-orange-500 scale-125' 
                    : 'bg-white/60 dark:bg-white/40 hover:bg-white/80 dark:hover:bg-white/60 hover:scale-110'
                }`}
                type="button"
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                role="tab"
                aria-selected={index === selectedIndex}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedCarousel;